"use client";

import Link from "next/link";
import { ArrowRight, BadgePercent } from "lucide-react";
import { useEffect } from "react";
import { CategoryPills } from "@/src/components/CategoryPills";
import { HeroStage } from "@/src/components/HeroStage";
import { NewArrivalsCarousel } from "@/src/components/NewArrivalsCarousel";
import { Newsletter } from "@/src/components/Newsletter";
import { Reviews } from "@/src/components/Reviews";
import { TrustBar } from "@/src/components/TrustBar";
import {
  customerReviews,
  dealHighlights,
  heroContent,
  homeCategories,
  homeProducts as fallbackHomeProducts,
  newsletterBenefits,
  trustItems,
  type HomeProduct,
} from "@/src/data/home";

type HomePageProps = {
  products?: HomeProduct[];
};

export function HomePage({ products = fallbackHomeProducts }: HomePageProps) {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]"),
    );
    if (!nodes.length) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    nodes.forEach((node, index) => {
      node.style.setProperty(
        "--reveal-delay",
        `${Math.min(index * 70, 320)}ms`,
      );
      observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-shell -mx-4 overflow-hidden rounded-[2rem] border border-slate-200/75 px-4 py-6 text-slate-700 sm:-mx-6 sm:px-6 sm:py-8 lg:-mx-8 lg:px-8 lg:py-10">
      <HeroStage content={heroContent} />

      <section
        className="home-panel home-panel--accent mt-9 p-5 sm:p-6"
        data-reveal={true}
      >
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="section-kicker">Neu im Sortiment</p>
            <h2 className="mt-3 font-heading text-2xl font-semibold text-slate-900 sm:text-[1.9rem]">
              Frische Drops für dein Setup
            </h2>
          </div>
          <Link
            href="/produkte?sort=newest"
            className="home-focus inline-flex items-center gap-2 rounded-xl border border-cyan-500/35 bg-cyan-100/75 px-4 py-2 text-sm font-semibold text-cyan-900 transition hover:-translate-y-0.5 hover:border-cyan-500/55 hover:bg-cyan-100"
          >
            Alle Neuheiten
            <ArrowRight className="h-4 w-4" />
          </Link>
        </header>
        <NewArrivalsCarousel products={products} />
      </section>

      <section
        className="home-panel mt-8 p-5 sm:p-6"
        data-reveal={true}
        aria-labelledby="home-categories-title"
      >
        <header className="mb-6">
          <p className="section-kicker">Sortimente</p>
          <h2
            id="home-categories-title"
            className="mt-3 font-heading text-2xl font-semibold text-slate-900 sm:text-[1.85rem]"
          >
            Finde dein Setup in wenigen Klicks
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Von Grinder bis Pflegeprodukte: klar sortiert für schnellen Zugriff.
          </p>
        </header>
        <CategoryPills categories={homeCategories} />
      </section>

      <section
        className="home-panel mt-8 p-5 sm:p-6"
        data-reveal={true}
        aria-labelledby="home-deals-title"
      >
        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="section-kicker">
              <BadgePercent className="h-4 w-4" />
              Deals
            </p>
            <h2
              id="home-deals-title"
              className="mt-3 font-heading text-2xl font-semibold text-slate-900 sm:text-[1.85rem]"
            >
              Mehrwert ohne Kompromisse
            </h2>
          </div>
          <Link
            href="/produkte?badge=sale"
            className="home-focus inline-flex items-center gap-2 rounded-xl border border-emerald-500/35 bg-emerald-100/80 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:-translate-y-0.5 hover:border-emerald-500/60 hover:bg-emerald-100"
          >
            Alle Angebote
            <ArrowRight className="h-4 w-4" />
          </Link>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          {dealHighlights.map((highlight) => (
            <article
              key={highlight.id}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/85 bg-white/74 p-5 shadow-[0_12px_26px_rgba(30,41,59,0.1)] transition hover:-translate-y-1 hover:border-cyan-500/35"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-cyan-300/28 blur-2xl" />
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                {highlight.metric}
              </p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">
                {highlight.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {highlight.copy}
              </p>
              <a
                href={highlight.href}
                className="home-focus mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 transition group-hover:translate-x-0.5 group-hover:text-emerald-800"
              >
                Zum Angebot
                <ArrowRight className="h-4 w-4" />
              </a>
            </article>
          ))}
        </div>
      </section>

      <section
        className="home-panel mt-8 p-5 sm:p-6"
        data-reveal={true}
        aria-labelledby="home-trust-title"
      >
        <header className="mb-6">
          <p className="section-kicker">Vertrauen</p>
          <h2
            id="home-trust-title"
            className="mt-3 font-heading text-2xl font-semibold text-slate-900 sm:text-[1.85rem]"
          >
            Verlässlich vom ersten Klick bis zur Lieferung
          </h2>
        </header>
        <TrustBar items={trustItems} />
      </section>

      <section
        className="home-panel mt-8 p-5 sm:p-6"
        data-reveal={true}
        aria-labelledby="home-reviews-title"
      >
        <header className="mb-6">
          <p className="section-kicker">Google-Bewertungen</p>
          <h2
            id="home-reviews-title"
            className="mt-3 font-heading text-2xl font-semibold text-slate-900 sm:text-[1.85rem]"
          >
            Google Rezensionen
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Hier stehen die öffentlich sichtbaren Google-Rezensionen als
            Originalzitate (nicht übersetzt) zu Echt Grandios in Gummersbach.
          </p>
        </header>
        <Reviews reviews={customerReviews} />
      </section>

      <section
        className="mt-8"
        data-reveal={true}
        aria-labelledby="home-newsletter-title"
      >
        <h2 id="home-newsletter-title" className="sr-only">
          Newsletter
        </h2>
        <Newsletter benefits={newsletterBenefits} />
      </section>

      <section className="home-panel mt-8 p-4 sm:p-5" data-reveal={true}>
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500">
          Schnellzugriff
        </p>
        <div className="mt-3 flex flex-wrap gap-2.5">
          <Link
            href="/impressum"
            className="home-focus rounded-full border border-slate-300/80 bg-white/78 px-3.5 py-1.5 text-xs text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50"
          >
            Impressum
          </Link>
          <Link
            href="/datenschutz"
            className="home-focus rounded-full border border-slate-300/80 bg-white/78 px-3.5 py-1.5 text-xs text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50"
          >
            Datenschutz
          </Link>
          <Link
            href="/agb"
            className="home-focus rounded-full border border-slate-300/80 bg-white/78 px-3.5 py-1.5 text-xs text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50"
          >
            AGB
          </Link>
          <Link
            href="/kontakt"
            className="home-focus rounded-full border border-slate-300/80 bg-white/78 px-3.5 py-1.5 text-xs text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50"
          >
            Kontakt
          </Link>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
